export class DotNetNumberFormat {
    public NegativeSign: string;
    public PositiveSign: string;
    public NegativeInfinitySymbol: string;
    public PositiveInfinitySymbol: string;
    public NaNSymbol: string;
    public CurrencyDecimalDigits: number;
    public CurrencyGroupSizes: number[];
    public CurrencyNegativePattern: number;
    public CurrencyPositivePattern: number;
    public CurrencyDecimalSeparator: string;
    public CurrencyGroupSeparator: string;
    public CurrencySymbol: string;
    public NumberDecimalDigits: number;
    public NumberGroupSizes: number[];
    public NumberNegativePattern: number;
    public NumberDecimalSeparator: string;
    public NumberGroupSeparator: string;
    public PercentDecimalDigits: number;
    public PercentGroupSizes: number[];
    public PercentNegativePattern: number;
    public PercentPositivePattern: number;
    public PercentDecimalSeparator: string;
    public PercentGroupSeparator: string;
    public PercentSymbol: string;
    public PerMilleSymbol: string;
}
export class DotNetCalendar {
    public GetEra: (d: Date) => number;
    public Eras: number[];
    public EraNames: string[];
}
export class DotNetDateTimeFormat {
    public GetEraName: (era: number) => string;
    public Calendar: DotNetCalendar;
    public NativeCalendarName: string;
    public AbbreviatedDayNames: string[];
    public DayNames: string[];
    public ShortestDayNames: string[];
    public MonthNames: string[];
    public MonthGenitiveNames: string[];
    public AbbreviatedMonthNames: string[];
    public AbbreviatedMonthGenitiveNames: string[];
    public AMDesignator: string;
    public PMDesignator: string;
    public DateSeparator: string;
    public TimeSeparator: string;
    public CalendarWeekRule: number;
    public FirstDayOfWeek: number;
    public FullDateTimePattern: string;
    public LongDatePattern: string;
    public LongTimePattern: string;
    public MonthDayPattern: string;
    public RFC1123Pattern: string;
    public ShortDatePattern: string;
    public ShortTimePattern: string;
    public SortableDateTimePattern: string;
    public YearMonthPattern: string;
    public UniversalSortableDateTimePattern: string;
    public ShortQuarter: string;
    public LongQuarter: string;
    public Quarters: string[];
}

export default class FormatDotNetStyle {
    public static numberCultures = {};
    private numberCulture: DotNetNumberFormat = null;
    public static dateTimeCultures = {};
    private static dateTimeCultureEnUS: DotNetDateTimeFormat = null;
    private dateTimeCulture: DotNetDateTimeFormat = null;

    public static decimalCode: string = '\x01';
    public static groupCode: string = '\x02';
    public static zeroCode: string = '\x03';
    public static poundCode: string = '\x04';
    public static percentCode: string = '\x05';
    public static permilleCode: string = '\x06';
    public static upperECode: string = '\x1F';
    public static lowerECode: string = '\x1E';
    public static sectionCode: string = '\x09';
    private static defaultGroupSizes: number[] = [3];
    public static EE: string[] = [
        FormatDotNetStyle.upperECode + '+' + FormatDotNetStyle.zeroCode,
        FormatDotNetStyle.lowerECode + '+' + FormatDotNetStyle.zeroCode,
        FormatDotNetStyle.upperECode + '-' + FormatDotNetStyle.zeroCode,
        FormatDotNetStyle.lowerECode + '-' + FormatDotNetStyle.zeroCode,
        FormatDotNetStyle.upperECode + FormatDotNetStyle.zeroCode,
        FormatDotNetStyle.lowerECode + FormatDotNetStyle.zeroCode
    ];
    private localizedGroupSize = FormatDotNetStyle.defaultGroupSizes;
    private localizedDecimalSymbol: string = '.';
    private localizedGroupSymbol: string = ',';
    private localizedPercentSymbol: string = '%';
    private localizedPermilleSymbol: string = '‰';
    private zeroSymbol: string = '0';
    private poundSymbol: string = '#';
    private percentSymbol: string = '%';
    private permilleSymbol: string = '‰';
    private sectionSymbol: string = ';';

    private static minusCode: string = '\x02';
    private static pCode: string = '\x03';
    private static dCode: string = '\x04';
    private static ddCode: string = '\x04\x04';
    private static dddCode: string = '\x04\x04\x04';
    private static ddddCode: string = '\x04\x04\x04\x04';
    private static fCode: string = '\x05';
    private static ffCode: string = '\x05\x05';
    private static fffCode: string = '\x05\x05\x05';
    private static ffffCode: string = '\x05\x05\x05\x05';
    private static fffffCode: string = '\x05\x05\x05\x05\x05';
    private static ffffffCode: string = '\x05\x05\x05\x05\x05\x05';
    private static fffffffCode: string = '\x05\x05\x05\x05\x05\x05\x05';
    private static FCode: string = '\x06';
    private static FFCode: string = '\x06\x06';
    private static FFFCode: string = '\x06\x06\x06';
    private static FFFFCode: string = '\x06\x06\x06\x06';
    private static FFFFFCode: string = '\x06\x06\x06\x06\x06';
    private static FFFFFFCode: string = '\x06\x06\x06\x06\x06\x06';
    private static FFFFFFFCode: string = '\x06\x06\x06\x06\x06\x06\x06';
    private static gCode: string = '\x08';
    private static ggCode: string = '\x08\x08';
    private static hCode: string = '\x0B';
    private static hhCode: string = '\x0B\x0B';
    private static HCode: string = '\x0C';
    private static HHCode: string = '\x0C\x0C';
    private static KCode: string = '\x0E';
    private static mCode: string = '\x0F';
    private static mmCode: string = '\x0F\x0F';
    private static MCode: string = '\x10';
    private static MMCode: string = '\x10\x10';
    private static MMMCode: string = '\x10\x10\x10';
    private static MMMMCode: string = '\x10\x10\x10\x10';
    private static sCode: string = '\x11';
    private static ssCode: string = '\x11\x11';
    private static tCode: string = '\x12';
    private static ttCode: string = '\x12\x12';
    private static yCode: string = '\x13';
    private static yyCode: string = '\x13\x13';
    private static yyyCode: string = '\x13\x13\x13';
    private static yyyyCode: string = '\x13\x13\x13\x13';
    private static yyyyyCode: string = '\x13\x13\x13\x13\x13';
    private static zCode: string = '\x14';
    private static zzCode: string = '\x14\x14';
    private static zzzCode: string = '\x14\x14\x14';
    private static defaultCultureCode: string = 'en-us';

    private position: number = 0;
    private actualPosition: number = 0;
    private source: string = '';
    private digitCount = 0;
    private static trimEnd0(s: string): string {
        if (s.length == 0) return s;
        let len = s.length - 1;
        while (len >= 0 && s[len] == '0') len--;
        if (len < s.length) return s.substr(0, len + 1);
        return s;
    }
    private static getDigitAt(s: string, i: number): number {
        const c = s.charCodeAt(i);
        const d = c - '0'.charCodeAt(0);
        return d < 0 || d > 9 ? -1 : d;
    }
    private static isDigitAt(s: string, i: number): boolean {
        const c = s.charCodeAt(i);
        return c >= '0'.charCodeAt(0) && c <= '9'.charCodeAt(0);
    }
    private static isLetterAt(s: string, i: number): boolean {
        const c = s.charCodeAt(i);
        return c > 128 || (c < 128 && ((c >= 'A'.charCodeAt(0) && c <= 'Z'.charCodeAt(0)) || (c >= 'a'.charCodeAt(0) && c <= 'z'.charCodeAt(0))));
    }
    private static pad0(o: number, n: number): string {
        let s = Math.abs(o).toString();
        const m = o < 0;
        for (let i = s.length; i < n; i++) s = '0' + s;
        if (m) s = '-' + s;
        return s;
    }
    private static pad0t(o: number, n: number): string {
        return FormatDotNetStyle.trimEnd0(FormatDotNetStyle.pad0(o, n));
    }
    private match(format: string, code: string): boolean {
        const i = format.indexOf(code, this.position);
        if (i != this.position) return false;
        this.position += code.length;
        return true;
    }
    private trimF(s: string): string {
        if (s == '') return s;
        const last = s.length - 1;
        if (s[last] == '.' || s.substr(last, 1) == this.numberCulture.NumberDecimalSeparator) s = s.substr(0, last);
        return s;
    }
    public static findIn(s: string, ss: string[]): number {
        for (let i = 0; i < ss.length; i++) if (s.toUpperCase() == ss[i].toUpperCase()) return i;
        return -1;
    }

    constructor(cultureCode: string = FormatDotNetStyle.defaultCultureCode) {
        cultureCode = cultureCode.toLowerCase();
        this.numberCulture = FormatDotNetStyle.numberCultures[cultureCode];
        if (this.numberCulture == null && cultureCode.length > 2) {
            cultureCode = cultureCode.substr(0, 2);
            this.numberCulture = FormatDotNetStyle.numberCultures[cultureCode];
        }
        if (this.numberCulture == null) this.numberCulture = FormatDotNetStyle.numberCultures[FormatDotNetStyle.defaultCultureCode];

        FormatDotNetStyle.dateTimeCultureEnUS = FormatDotNetStyle.dateTimeCultures[FormatDotNetStyle.defaultCultureCode];
        this.dateTimeCulture = FormatDotNetStyle.dateTimeCultures[cultureCode];
        if (this.dateTimeCulture == null && cultureCode.length > 2) {
            cultureCode = cultureCode.substr(0, 2);
            this.dateTimeCulture = FormatDotNetStyle.dateTimeCultures[cultureCode];
        }
        if (this.dateTimeCulture == null) this.dateTimeCulture = FormatDotNetStyle.dateTimeCultures[FormatDotNetStyle.defaultCultureCode];
    }
    public getMarkerWord(format: string): string {
        const p = this.position;
        const s = this.source;
        this.actualPosition = 0;
        this.source = format;
        const w = this.scanWord();
        this.actualPosition = p;
        this.source = s;
        return w;
    }
    public static findWord(s: string, w: string, start: number): number {
        if (w.length == 0) return -1;
        let p = s.indexOf(w, start);
        while (p >= 0) {
            if ((p == 0 || !FormatDotNetStyle.isLetterAt(s, p - 1)) && (p + w.length >= s.length || !FormatDotNetStyle.isLetterAt(s, p + w.length)))
                return p;
            p = s.indexOf(w, p + w.length);
        }
        return -1;
    }
    public static replaceWord(s: string, w: string, r: string, start: number): string {
        if (w.length == 0) return s;
        let p = FormatDotNetStyle.findWord(s, w, start);
        while (p >= 0) {
            s = s.substr(0, p) + r + s.substr(p + w.length);
            p = FormatDotNetStyle.findWord(s, w, p + r.length);
        }
        return s;
    }
    public static findAny(datetime: string, list: string[]): number {
        datetime = datetime.toUpperCase();
        for (let i = 0; i < list.length; i++) {
            if (list[i].length < 3) continue;
            const n = list[i].toUpperCase();
            if (FormatDotNetStyle.findWord(datetime, n, 0) >= 0) return i;
        }
        return -1;
    }
    public static findMonthByName(datetime: string, c: DotNetDateTimeFormat): number {
        let month = FormatDotNetStyle.findAny(datetime, c.MonthGenitiveNames);
        if (month < 0) month = FormatDotNetStyle.findAny(datetime, c.MonthNames);
        if (month < 0) month = FormatDotNetStyle.findAny(datetime, c.AbbreviatedMonthGenitiveNames);
        if (month < 0) month = FormatDotNetStyle.findAny(datetime, c.AbbreviatedMonthNames);
        return month;
    }
    public static findMonthInCulture(m: string, c: DotNetDateTimeFormat): number {
        let month = FormatDotNetStyle.findIn(m, c.MonthGenitiveNames);
        if (month < 0) month = FormatDotNetStyle.findIn(m, c.MonthNames);
        if (month < 0) month = FormatDotNetStyle.findIn(m, c.AbbreviatedMonthGenitiveNames);
        if (month < 0) month = FormatDotNetStyle.findIn(m, c.AbbreviatedMonthNames);
        if (month >= 0 || c == FormatDotNetStyle.dateTimeCultureEnUS) return month;
        c = FormatDotNetStyle.dateTimeCultureEnUS;
        month = FormatDotNetStyle.findIn(m, c.MonthGenitiveNames);
        if (month < 0) month = FormatDotNetStyle.findIn(m, c.MonthNames);
        if (month < 0) month = FormatDotNetStyle.findIn(m, c.AbbreviatedMonthGenitiveNames);
        if (month < 0) month = FormatDotNetStyle.findIn(m, c.AbbreviatedMonthNames);
        return month;
    }

    public formatQuarter(format: string, quarter: number): string {
        if (format.indexOf('QQQQ') !== -1) {
            format = format.replaceAll('QQQQ', this.dateTimeCulture.LongQuarter + ' ' + this.dateTimeCulture.Quarters[quarter - 1]);
        }

        if (format.indexOf('QQ') !== -1) {
            format = format.replaceAll('QQ', this.dateTimeCulture.ShortQuarter + ' ' + this.dateTimeCulture.Quarters[quarter - 1]);
        }
        return format;
    }

    public formatDateTime(format: string, datetime: Date): string {
        if (format.length > 1 || 'dDfFgGmMroORstTuUyY'.indexOf(format) < 0) return this.formatCustomDateTime(format, datetime);
        if (format.length == 0) format = 'G';
        if (format == 'd') return this.formatCustomDateTime(this.dateTimeCulture.ShortDatePattern, datetime);
        if (format == 'D') return this.formatCustomDateTime(this.dateTimeCulture.LongDatePattern, datetime);
        if (format == 'f')
            return this.formatCustomDateTime(this.dateTimeCulture.LongDatePattern + ' ' + this.dateTimeCulture.ShortTimePattern, datetime);
        if (format == 'F') return this.formatCustomDateTime(this.dateTimeCulture.FullDateTimePattern, datetime);
        if (format == 'g')
            return this.formatCustomDateTime(this.dateTimeCulture.ShortDatePattern + ' ' + this.dateTimeCulture.ShortTimePattern, datetime);
        if (format == 'm' || format == 'M') return this.formatCustomDateTime(this.dateTimeCulture.MonthDayPattern, datetime);
        if (format == 'o' || format == 'O') return this.formatCustomDateTime("yyyy'-'MM'-'dd'T'HH':'mm':'ss'.'fffffffK", datetime);
        if (format == 'r' || format == 'R') {
            const b = this.dateTimeCulture;
            this.dateTimeCulture = FormatDotNetStyle.dateTimeCultureEnUS;
            const r = this.formatCustomDateTime(this.dateTimeCulture.RFC1123Pattern, datetime);
            this.dateTimeCulture = b;
            return r;
        }
        if (format == 's') return this.formatCustomDateTime(this.dateTimeCulture.SortableDateTimePattern, datetime);
        if (format == 't') return this.formatCustomDateTime(this.dateTimeCulture.ShortTimePattern, datetime);
        if (format == 'T') return this.formatCustomDateTime(this.dateTimeCulture.LongTimePattern, datetime);
        if (format == 'u') return this.formatCustomDateTime(this.dateTimeCulture.UniversalSortableDateTimePattern, datetime);
        if (format == 'U') {
            datetime = new Date(
                datetime.getUTCFullYear(),
                datetime.getUTCMonth(),
                datetime.getUTCDate(),
                datetime.getUTCHours(),
                datetime.getUTCMinutes(),
                datetime.getUTCSeconds(),
                datetime.getUTCMilliseconds()
            );
            return this.formatCustomDateTime(this.dateTimeCulture.FullDateTimePattern, datetime);
        }
        if (format == 'y' || format == 'Y') return this.formatCustomDateTime(this.dateTimeCulture.YearMonthPattern, datetime);
        /*if (format == "G")*/
        return this.formatCustomDateTime(this.dateTimeCulture.ShortDatePattern + ' ' + this.dateTimeCulture.LongTimePattern, datetime);
    }
    public formatCustomDateTime(format: string, datetime: Date): string {
        const unescaped = this.unescapeDateTime(format);
        let s = '';
        this.position = 0;
        while (this.position < unescaped.length) {
            if (this.match(unescaped, FormatDotNetStyle.ddddCode)) {
                s += this.dateTimeCulture.DayNames[datetime.getDay()];
                while (this.match(unescaped, FormatDotNetStyle.dCode));
            } else if (this.match(unescaped, FormatDotNetStyle.dddCode)) s += this.dateTimeCulture.AbbreviatedDayNames[datetime.getDay()];
            else if (this.match(unescaped, FormatDotNetStyle.ddCode)) s += FormatDotNetStyle.pad0(datetime.getDate(), 2);
            else if (this.match(unescaped, FormatDotNetStyle.dCode)) s += datetime.getDate();
            else if (this.match(unescaped, FormatDotNetStyle.fffffffCode)) s += FormatDotNetStyle.pad0(datetime.getMilliseconds() * 10000, 7);
            else if (this.match(unescaped, FormatDotNetStyle.ffffffCode)) s += FormatDotNetStyle.pad0(datetime.getMilliseconds() * 1000, 6);
            else if (this.match(unescaped, FormatDotNetStyle.fffffCode)) s += FormatDotNetStyle.pad0(datetime.getMilliseconds() * 100, 5);
            else if (this.match(unescaped, FormatDotNetStyle.ffffCode)) s += FormatDotNetStyle.pad0(datetime.getMilliseconds() * 10, 4);
            else if (this.match(unescaped, FormatDotNetStyle.fffCode)) s += FormatDotNetStyle.pad0(datetime.getMilliseconds(), 3);
            else if (this.match(unescaped, FormatDotNetStyle.ffCode)) s += FormatDotNetStyle.pad0(Math.floor(datetime.getMilliseconds() / 10.0), 2);
            else if (this.match(unescaped, FormatDotNetStyle.fCode)) s += Math.floor(datetime.getMilliseconds() / 100.0);
            else if (this.match(unescaped, FormatDotNetStyle.FFFFFFFCode)) {
                s += FormatDotNetStyle.pad0t(datetime.getMilliseconds() * 10000, 7);
                s = this.trimF(s);
            } else if (this.match(unescaped, FormatDotNetStyle.FFFFFFCode)) {
                s += FormatDotNetStyle.pad0t(datetime.getMilliseconds() * 1000, 6);
                s = this.trimF(s);
            } else if (this.match(unescaped, FormatDotNetStyle.FFFFFCode)) {
                s += FormatDotNetStyle.pad0t(datetime.getMilliseconds() * 100, 5);
                s = this.trimF(s);
            } else if (this.match(unescaped, FormatDotNetStyle.FFFFCode)) {
                s += FormatDotNetStyle.pad0t(datetime.getMilliseconds() * 10, 4);
                s = this.trimF(s);
            } else if (this.match(unescaped, FormatDotNetStyle.FFFCode)) {
                s += FormatDotNetStyle.pad0t(datetime.getMilliseconds(), 3);
                s = this.trimF(s);
            } else if (this.match(unescaped, FormatDotNetStyle.FFCode)) {
                s += FormatDotNetStyle.pad0t(Math.floor(datetime.getMilliseconds() / 10.0), 2);
                s = this.trimF(s);
            } else if (this.match(unescaped, FormatDotNetStyle.FCode)) {
                s += FormatDotNetStyle.pad0t(Math.floor(datetime.getMilliseconds() / 100.0), 1);
                s = this.trimF(s);
            } else if (this.match(unescaped, FormatDotNetStyle.ggCode) || this.match(unescaped, FormatDotNetStyle.gCode)) {
                s += this.dateTimeCulture.GetEraName(this.dateTimeCulture.Calendar.GetEra(datetime));
                while (this.match(unescaped, FormatDotNetStyle.gCode));
            } else if (this.match(unescaped, FormatDotNetStyle.hhCode)) {
                s += FormatDotNetStyle.pad0(FormatDotNetStyle.geth112(datetime.getHours()), 2);
                while (this.match(unescaped, FormatDotNetStyle.hCode));
            } else if (this.match(unescaped, FormatDotNetStyle.hCode)) s += FormatDotNetStyle.geth112(datetime.getHours());
            else if (this.match(unescaped, FormatDotNetStyle.HHCode)) {
                s += FormatDotNetStyle.pad0(datetime.getHours(), 2);
                while (this.match(unescaped, FormatDotNetStyle.HCode));
            } else if (this.match(unescaped, FormatDotNetStyle.HCode)) s += datetime.getHours();
            else if (this.match(unescaped, FormatDotNetStyle.KCode)) {
            } else if (this.match(unescaped, FormatDotNetStyle.mmCode)) {
                s += FormatDotNetStyle.pad0(datetime.getMinutes(), 2);
                while (this.match(unescaped, FormatDotNetStyle.mCode));
            } else if (this.match(unescaped, FormatDotNetStyle.mCode)) s += datetime.getMinutes();
            else if (this.match(unescaped, FormatDotNetStyle.MMMMCode)) {
                s += FormatDotNetStyle.monthDayInFormat(unescaped)
                    ? this.dateTimeCulture.MonthGenitiveNames[datetime.getMonth()]
                    : this.dateTimeCulture.MonthNames[datetime.getMonth()];
                while (this.match(unescaped, FormatDotNetStyle.MCode));
            } else if (this.match(unescaped, FormatDotNetStyle.MMMCode)) {
                s += FormatDotNetStyle.monthDayInFormat(unescaped)
                    ? this.dateTimeCulture.AbbreviatedMonthGenitiveNames[datetime.getMonth()]
                    : this.dateTimeCulture.AbbreviatedMonthNames[datetime.getMonth()];
            } else if (this.match(unescaped, FormatDotNetStyle.MMCode)) {
                s += FormatDotNetStyle.pad0(datetime.getMonth() + 1, 2);
            } else if (this.match(unescaped, FormatDotNetStyle.MCode)) s += datetime.getMonth() + 1;
            else if (this.match(unescaped, FormatDotNetStyle.ssCode)) {
                s += FormatDotNetStyle.pad0(datetime.getSeconds(), 2);
                while (this.match(unescaped, FormatDotNetStyle.sCode));
            } else if (this.match(unescaped, FormatDotNetStyle.sCode)) s += datetime.getSeconds();
            else if (this.match(unescaped, FormatDotNetStyle.ttCode)) {
                s += datetime.getHours() < 12 ? this.dateTimeCulture.AMDesignator : this.dateTimeCulture.PMDesignator;
                while (this.match(unescaped, FormatDotNetStyle.tCode));
            } else if (this.match(unescaped, FormatDotNetStyle.tCode)) {
                const am = this.dateTimeCulture.AMDesignator.length > 0 ? '' + this.dateTimeCulture.AMDesignator[0] : '';
                const pm = this.dateTimeCulture.PMDesignator.length > 0 ? '' + this.dateTimeCulture.PMDesignator[0] : '';
                s += datetime.getHours() < 12 ? am : pm;
            } else if (this.match(unescaped, FormatDotNetStyle.yyyyyCode)) {
                let n = 0;
                while (this.match(unescaped, FormatDotNetStyle.yCode)) n++;
                s += FormatDotNetStyle.pad0(datetime.getFullYear(), 5 + n);
            } else if (this.match(unescaped, FormatDotNetStyle.yyyyCode)) s += FormatDotNetStyle.pad0(datetime.getFullYear(), 4);
            else if (this.match(unescaped, FormatDotNetStyle.yyyCode)) s += FormatDotNetStyle.pad0(datetime.getFullYear(), 3);
            else if (this.match(unescaped, FormatDotNetStyle.yyCode)) s += FormatDotNetStyle.pad0(datetime.getFullYear() % 100, 2);
            else if (this.match(unescaped, FormatDotNetStyle.yCode)) s += datetime.getFullYear() % 100;
            else if (this.match(unescaped, FormatDotNetStyle.zzzCode)) {
                if (datetime.getTimezoneOffset() < 0) s += '+';
                s +=
                    FormatDotNetStyle.pad0((-1 * datetime.getTimezoneOffset()) / 60, 2) +
                    ':' +
                    FormatDotNetStyle.pad0(datetime.getTimezoneOffset() % 60, 2);
                while (this.match(unescaped, FormatDotNetStyle.zCode));
            } else if (this.match(unescaped, FormatDotNetStyle.zzCode)) {
                if (datetime.getTimezoneOffset() < 0) s += '+';
                s += FormatDotNetStyle.pad0((-1 * datetime.getTimezoneOffset()) / 60, 2);
            } else if (this.match(unescaped, FormatDotNetStyle.zCode)) {
                if (datetime.getTimezoneOffset() < 0) s += '+';
                s += (-1 * datetime.getTimezoneOffset()) / 60;
            } else if (this.match(unescaped, FormatDotNetStyle.pCode)) {
            } else {
                s += unescaped[this.position];
                this.position++;
            }
        }
        return s;
    }
    private static geth112(h023: number): number {
        if (h023 == 0 || h023 == 12) return 12;
        if (h023 < 12) return h023;
        return h023 - 12;
    }
    private static monthDayInFormat(unescaped: string): boolean {
        for (let i = 0; i < unescaped.length; i++) {
            let cp = ' ';
            let cnn = ' ';
            if (i > 0) cp = unescaped.substr(i - 1, 1);
            if (i > 2 && i < unescaped.length - 2) cnn = unescaped.substr(i + 2, 1);
            if (unescaped.indexOf(FormatDotNetStyle.dCode, i) == i && cp != FormatDotNetStyle.dCode && cnn != FormatDotNetStyle.dCode) return true;
        }
        return false;
    }
    private unescapeDateTime(format: string): string {
        let unescaped = '';
        let sq = false;
        let dq = false;
        for (let i = 0; i < format.length; i++) {
            if (sq) {
                if (format[i] == "'") sq = false;
                else unescaped += format[i];
            } else if (dq) {
                if (format[i] == '"') dq = false;
                else unescaped += format[i];
            } else {
                if (format[i] == "'") {
                    sq = true;
                } else if (format[i] == '"') {
                    dq = true;
                } else if (format[i] == '\\') {
                    if (i < format.length - 1) {
                        i++;
                        unescaped += format[i];
                    }
                } else {
                    if (format[i] == '%') unescaped += FormatDotNetStyle.pCode;
                    else if (format[i] == '/') unescaped += this.dateTimeCulture.DateSeparator;
                    else if (format[i] == ':') unescaped += this.dateTimeCulture.TimeSeparator;
                    else if (format[i] == 'd') unescaped += FormatDotNetStyle.dCode;
                    else if (format[i] == 'f') unescaped += FormatDotNetStyle.fCode;
                    else if (format[i] == 'F') unescaped += FormatDotNetStyle.FCode;
                    else if (format[i] == 'g') unescaped += FormatDotNetStyle.gCode;
                    else if (format[i] == 'h') unescaped += FormatDotNetStyle.hCode;
                    else if (format[i] == 'H') unescaped += FormatDotNetStyle.HCode;
                    else if (format[i] == 'K') unescaped += FormatDotNetStyle.KCode;
                    else if (format[i] == 'm') unescaped += FormatDotNetStyle.mCode;
                    else if (format[i] == 'M') unescaped += FormatDotNetStyle.MCode;
                    else if (format[i] == 's') unescaped += FormatDotNetStyle.sCode;
                    else if (format[i] == 't') unescaped += FormatDotNetStyle.tCode;
                    else if (format[i] == 'y') unescaped += FormatDotNetStyle.yCode;
                    else if (format[i] == 'z') unescaped += FormatDotNetStyle.zCode;
                    else unescaped += format[i];
                }
            }
        }
        if (unescaped.indexOf(FormatDotNetStyle.pCode) >= 0) {
            const buffer = unescaped;
            unescaped = '';
            for (let i = 0; i < buffer.length; i++) {
                if (buffer.indexOf(FormatDotNetStyle.pCode, i) == i) {
                    if (i >= buffer.length - 1) continue;
                    if ('dfFghHKmMstyz'.indexOf(format[i + 1]) < 0) continue;
                    unescaped += '' + FormatDotNetStyle.pCode + buffer[i + 1] + '' + FormatDotNetStyle.pCode;
                    i++;
                } else {
                    unescaped += buffer[i];
                }
            }
        }
        return unescaped;
    }

    public parseDateTime(datetime: string, format: string = null, { withCheck = false } = {}): Date {
        let year = -1;
        let month = -1;
        let day = -1;
        let hour = -1;
        let minute = -1;
        let second = -1;
        let millisecond = -1.0;
        if (format == null) {
            let datePattern = '';
            month = FormatDotNetStyle.findMonthByName(datetime, this.dateTimeCulture);
            if (month >= 0) datePattern = this.dateTimeCulture.LongDatePattern;
            if (datePattern == '') {
                month = FormatDotNetStyle.findMonthByName(datetime, FormatDotNetStyle.dateTimeCultureEnUS);
                if (month >= 0) datePattern = FormatDotNetStyle.dateTimeCultureEnUS.LongDatePattern;
            }
            if (datePattern == '') {
                const m = this.getMarkerWord(this.unescapeDateTime(this.dateTimeCulture.LongDatePattern));
                if (m.length > 0 && FormatDotNetStyle.findWord(datetime, m, 0) >= 0) datePattern = this.dateTimeCulture.LongDatePattern;
            }
            if (datePattern == '') {
                if (datetime.indexOf(this.dateTimeCulture.DateSeparator) >= 0) datePattern = this.dateTimeCulture.ShortDatePattern;
                else if (datetime.indexOf(FormatDotNetStyle.dateTimeCultureEnUS.DateSeparator) >= 0)
                    datePattern = FormatDotNetStyle.dateTimeCultureEnUS.ShortDatePattern;
            }
            let timePattern = '';
            if (datetime.indexOf(this.dateTimeCulture.TimeSeparator) >= 0) timePattern = this.dateTimeCulture.LongTimePattern;
            else if (datetime.indexOf(FormatDotNetStyle.dateTimeCultureEnUS.TimeSeparator) >= 0)
                timePattern = FormatDotNetStyle.dateTimeCultureEnUS.LongTimePattern;
            format = datePattern + ' ' + timePattern;
        }

        let marker = this.getMarkerWord(this.unescapeDateTime(format));
        while (marker != '') {
            format = FormatDotNetStyle.replaceWord(format, marker, ' ', 0);
            datetime = FormatDotNetStyle.replaceWord(datetime, marker, ' ', 0);
            marker = this.getMarkerWord(this.unescapeDateTime(format));
        }

        this.source = datetime.toUpperCase();
        const unescaped = this.unescapeDateTime(format);
        this.position = 0;
        this.actualPosition = 0;
        while (this.position < unescaped.length && this.actualPosition < datetime.length) {
            if (this.match(unescaped, FormatDotNetStyle.yyyyCode) || this.match(unescaped, FormatDotNetStyle.yyyCode)) {
                year = this.scanNumber();
                if (year < 0) break;
                while (this.match(unescaped, FormatDotNetStyle.yCode));
            } else if (this.match(unescaped, FormatDotNetStyle.yyCode) || this.match(unescaped, FormatDotNetStyle.yCode)) {
                year = this.scanNumber();
                if (year < 0) break;
                if (this.digitCount <= 2) {
                    if (year < 20) year += 2000;
                    if (year < 100) year += 1900;
                }
            } else if (this.match(unescaped, FormatDotNetStyle.MMMCode)) {
                const m = this.scanWord();
                month = FormatDotNetStyle.findMonthInCulture(m, this.dateTimeCulture);
                if (month < 0) break;
                month++;
                while (this.match(unescaped, FormatDotNetStyle.MCode));
            } else if (this.match(unescaped, FormatDotNetStyle.MCode)) {
                month = this.scanNumber();
                while (this.match(unescaped, FormatDotNetStyle.MCode));
            } else if (this.match(unescaped, FormatDotNetStyle.dddCode)) {
                this.scanWord();
                while (this.match(unescaped, FormatDotNetStyle.dCode));
            } else if (this.match(unescaped, FormatDotNetStyle.dCode)) {
                day = this.scanNumber();
                if (day < 0) break;
                while (this.match(unescaped, FormatDotNetStyle.dCode));
            } else if (this.match(unescaped, FormatDotNetStyle.hCode) || this.match(unescaped, FormatDotNetStyle.HCode)) {
                hour = this.scanNumber();
                if (hour < 0 || hour > 23) break;
                while (this.match(unescaped, FormatDotNetStyle.hCode));
                while (this.match(unescaped, FormatDotNetStyle.HCode));
                if (hour > 0 && hour < 13) {
                    if (
                        (this.dateTimeCulture.PMDesignator.length > 0 &&
                            FormatDotNetStyle.findWord(datetime, this.dateTimeCulture.PMDesignator, this.actualPosition) >= 0) ||
                        FormatDotNetStyle.findWord(datetime, FormatDotNetStyle.dateTimeCultureEnUS.PMDesignator, this.actualPosition) >= 0
                    ) {
                        if (hour < 12) hour += 12;
                    } else if (
                        (this.dateTimeCulture.AMDesignator.length > 0 &&
                            FormatDotNetStyle.findWord(datetime, this.dateTimeCulture.AMDesignator, this.actualPosition) >= 0) ||
                        FormatDotNetStyle.findWord(datetime, FormatDotNetStyle.dateTimeCultureEnUS.AMDesignator, this.actualPosition) >= 0
                    ) {
                        if (hour == 12) hour = 0;
                    }
                }
            } else if (this.match(unescaped, FormatDotNetStyle.mCode)) {
                minute = this.scanNumber();
                if (minute < 0) break;
                while (this.match(unescaped, FormatDotNetStyle.mCode));
            } else if (this.match(unescaped, FormatDotNetStyle.sCode)) {
                second = this.scanNumber();
                if (second < 0) break;
                while (this.match(unescaped, FormatDotNetStyle.sCode));

                if (
                    this.actualPosition < this.source.length - 1 &&
                    ':,.'.indexOf(this.source[this.actualPosition]) >= 0 &&
                    (this.match(unescaped, FormatDotNetStyle.fCode) || this.match(unescaped, FormatDotNetStyle.FCode))
                ) {
                    millisecond = this.scanNumber();
                    if (millisecond < 0) break;
                    let o = 1;
                    while (this.match(unescaped, FormatDotNetStyle.fCode)) o++;
                    while (this.match(unescaped, FormatDotNetStyle.FCode)) o++;
                    millisecond /= Math.pow(10, o - 3);
                } else {
                    millisecond = 0;
                }
            } else {
                this.position++;
            }
        }

        if (year < 0 && month < 0 && day < 0 && hour < 0 && minute < 0 && second < 0 && millisecond < 0) return null;
        if (withCheck) {
            setDefault();
            const parts = [year, --month, day, hour, minute, second, millisecond];
            const date: Date = new Date(Number.NaN);
            for (let i = 0; i < FormatDotNetStyle.datePartNames.length; i++) {
                const partName = FormatDotNetStyle.datePartNames[i];
                const prevPartName = FormatDotNetStyle.datePartNames[i - 1];
                date[`set${partName}`](parts[i]);
                if (prevPartName && date[`get${prevPartName}`]() !== parts[i - 1]) {
                    console.log(partName, parts[i], datetime, format);
                    return new Date(Number.NaN);
                }
            }
            return date;
        } else {
            if (day > 31 && year <= 31) {
                const x = year;
                year = day;
                day = x;
            }
            if (month > 31 && year <= 12) {
                const x = year;
                year = month;
                month = x;
            }
            if (month > 12 && month <= 31 && day <= 12) {
                const x = month;
                month = day;
                day = x;
            }
            if (month > 12 && month <= 31 && day > 12 && year <= 12) {
                const x = month;
                month = year;
                year = month;
            }
            setDefault();
            return new Date(year, month - 1, day, hour, minute, second, millisecond);
        }

        function setDefault() {
            if (year < 0) year = 1;
            if (month < 0) month = 1;
            if (day < 0) day = 1;
            if (hour < 0) hour = 0;
            if (minute < 0) minute = 0;
            if (second < 0) second = 0;
            if (millisecond < 0) millisecond = 0;
        }
    }

    private static datePartNames = ['FullYear', 'Month', 'Date', 'Hours', 'Minutes', 'Seconds', 'Milliseconds'];

    public static toDigits(num: number): string {
        const s = num.toString().toUpperCase();
        let e = -1.0;
        let d = s;
        let ep = s.indexOf('E');
        if (ep >= 0) {
            d = s.substr(0, ep);
            if (s[ep + 1] == '+') ep++;
            e = parseInt(s.substr(ep + 1));
        }
        if (d.indexOf('.') >= 0) d = d.replace('.', '');
        if (e >= 0) {
            const pad = e + 1 - d.length;
            d += FormatDotNetStyle.formatMulti('0', pad);
        }
        return d;
    }
    public getNaNOrInfinitySymbol(d: number): string {
        if (d == Number.NEGATIVE_INFINITY) return this.numberCulture.NegativeInfinitySymbol;
        if (d == Number.POSITIVE_INFINITY) return this.numberCulture.PositiveInfinitySymbol;
        return this.numberCulture.NaNSymbol;
    }
    private static getActualPrecision(num: number): number {
        let precision = 0.0;
        const sampleE = num.toExponential().toUpperCase();
        const Ep = sampleE.indexOf('E');
        precision += Ep;
        if (sampleE[0] == '-') precision--;
        if (sampleE.indexOf('.') >= 0) precision--;
        if (sampleE[Ep + 1] == '-') precision += parseInt(sampleE.substr(Ep + 2));
        return precision;
    }
    private static getActualExponent(num: number): number {
        const sampleE = num.toExponential().toUpperCase();
        let Ep = sampleE.indexOf('E');
        if (sampleE[Ep + 1] == '+') Ep++;
        return parseInt(sampleE.substr(Ep + 1));
    }
    private static formatMulti(c: string, n: number): string {
        let s = '';
        for (let i = 0; i < n; i++) s += c;
        return s;
    }
    private static formatReverse(s: string): string {
        let reverse = '';
        for (let i = 0; i < s.length; i++) reverse = s[i] + reverse;
        return reverse;
    }
    private static formatCountNumeric(f: string): number {
        let p = 0;
        for (let i = 0; i < f.length; i++) if (f[i] == FormatDotNetStyle.zeroCode || f[i] == FormatDotNetStyle.poundCode) p++;
        return p;
    }
    private static formatRound(num: number, round: number): number {
        let m = 0;
        let a = Math.abs(num);
        while (Math.floor(a) != a && m < round) {
            a *= 10;
            m++;
        }
        const n = Math.floor(a);
        const f = a - n;
        a = n;
        if (f >= 0.5) a += 1;
        if (round >= 0) a /= Math.pow(10, m);
        if (num < 0) a = -a;
        return a;
    }
    private static formatCountPad(format: string, right: boolean): number {
        let pad = -1;
        if (right) format = FormatDotNetStyle.formatReverse(format);
        for (let i = 0; i < format.length; i++) {
            if (pad == -1 && format[i] == FormatDotNetStyle.zeroCode) pad = 0;
            if (pad != -1 && (format[i] == FormatDotNetStyle.zeroCode || format[i] == FormatDotNetStyle.poundCode)) pad++;
        }
        return pad < 0 ? 0 : pad;
    }
    private static formatInsertExtraSymbols(num: string, format: string, right: boolean): string {
        if (right) {
            format = FormatDotNetStyle.formatReverse(format);
            num = FormatDotNetStyle.formatReverse(num);
        }
        let firstDigitIndex = 0;
        for (let i = 0; i < format.length; i++)
            if (format[i] == FormatDotNetStyle.zeroCode || format[i] == FormatDotNetStyle.poundCode) {
                firstDigitIndex = i;
                break;
            }
        let formatDigits = 0;
        let actualPos = num.length - 1;
        let actualDigits = 0;
        for (let i = format.length - 1; i >= firstDigitIndex; i--) {
            if (format[i] == FormatDotNetStyle.zeroCode || format[i] == FormatDotNetStyle.poundCode) {
                formatDigits++;
            } else if (format[i] == FormatDotNetStyle.groupCode) {
            } else {
                while (actualDigits < formatDigits && actualPos >= 0) {
                    if (FormatDotNetStyle.isDigitAt(num, actualPos)) actualDigits++;
                    actualPos--;
                }
                num = num.substr(0, actualPos + 1) + format[i] + num.substr(actualPos + 1);
            }
        }
        if (firstDigitIndex > 0) num = format.substr(0, firstDigitIndex) + num;
        if (right) num = FormatDotNetStyle.formatReverse(num);
        return num;
    }
    private formatEncoded(encodedFormat: string, num: number, multipleFormats: boolean): string {
        if (encodedFormat.indexOf(FormatDotNetStyle.percentCode) >= 0) num *= Math.pow(10, 2);
        if (encodedFormat.indexOf(FormatDotNetStyle.permilleCode) >= 0) num *= Math.pow(10, 3);
        const originalnum = num;

        if (!isFinite(num)) return this.getNaNOrInfinitySymbol(num);

        let formatInteger = '';
        let formatFloat = '';
        let formatE = '';
        for (let j = 0; j < encodedFormat.length - 1; j++) {
            for (let i = 0; i < FormatDotNetStyle.EE.length; i++) {
                const eprefix = FormatDotNetStyle.EE[i];
                let e = true;
                for (let k = 0; k < eprefix.length && j + k < encodedFormat.length; k++)
                    if (encodedFormat[j + k] != eprefix[k]) {
                        e = false;
                        break;
                    }
                if (e) {
                    let l = j + eprefix.length;
                    while (l < encodedFormat.length && encodedFormat[l] == FormatDotNetStyle.zeroCode) l++;
                    formatE = encodedFormat
                        .substr(j, l - j)
                        .replace(FormatDotNetStyle.upperECode, 'E')
                        .replace(FormatDotNetStyle.lowerECode, 'e');
                    const ls = encodedFormat.substr(0, j).replace(FormatDotNetStyle.upperECode, 'E').replace(FormatDotNetStyle.lowerECode, 'e');
                    const rs = encodedFormat.substr(l).replace(FormatDotNetStyle.upperECode, 'E').replace(FormatDotNetStyle.lowerECode, 'e');
                    encodedFormat = ls + FormatDotNetStyle.upperECode + rs;
                    break;
                }
            }
            if (formatE.length > 0) break;
        }

        if (formatE.length == 0) encodedFormat = encodedFormat.replace(FormatDotNetStyle.upperECode, 'E').replace(FormatDotNetStyle.lowerECode, 'e');
        formatInteger = encodedFormat;

        const decimalPointPosition = encodedFormat.indexOf(FormatDotNetStyle.decimalCode);
        if (decimalPointPosition >= 0) {
            formatInteger = encodedFormat.substr(0, decimalPointPosition);
            formatFloat = encodedFormat.substr(decimalPointPosition + 1);
        }
        const fomatPrecision = FormatDotNetStyle.formatCountNumeric(formatFloat);
        let precision = fomatPrecision;
        let actualPrecision = FormatDotNetStyle.getActualPrecision(num);
        if (precision > actualPrecision) precision = actualPrecision;

        if (formatE.length > 0) {
            const d = FormatDotNetStyle.formatCountNumeric(formatInteger);
            const top = Math.pow(10, d);
            const bottom = Math.pow(10, d - 1);
            let m = 0;
            if (num != 0) {
                const negative = num < 0;
                num = Math.abs(num);
                while (num >= top) {
                    num /= 10;
                    m++;
                }
                while (num < bottom) {
                    num *= 10;
                    m--;
                }

                num = FormatDotNetStyle.formatRound(num, precision);
                if (num >= top) {
                    num /= 10;
                    m++;
                }

                if (negative) num = -num;
            }

            formatInteger = formatInteger.replace(FormatDotNetStyle.poundCode, FormatDotNetStyle.zeroCode);

            const zzf = formatE.substr(formatE.indexOf(FormatDotNetStyle.zeroCode));
            const zz = this.formatEncoded(zzf, m, false);
            formatE = formatE[0] + (formatE[1] == '+' && zz[0] != '-' ? '+' : '') + zz;
        }

        let scale = 0;
        for (let i = formatInteger.length - 1; i >= 0; i--)
            if (formatInteger[i] == FormatDotNetStyle.groupCode) scale++;
            else break;
        if (scale > 0) num /= Math.pow(10, scale * this.localizedGroupSize[0]);

        if (!isFinite(num)) return this.getNaNOrInfinitySymbol(num);

        actualPrecision = FormatDotNetStyle.getActualPrecision(num);
        precision = fomatPrecision;
        if (precision > actualPrecision) precision = actualPrecision;

        num = FormatDotNetStyle.formatRound(num, precision);
        if (originalnum != 0 && num == 0 && multipleFormats) return '0';
        if (FormatDotNetStyle.formatCountNumeric(encodedFormat) == 0) return (num < 0 && !multipleFormats ? '-' : '') + encodedFormat;

        let a = Math.abs(num);
        let n = Math.floor(a);

        let s = n == 0 ? '' : FormatDotNetStyle.toDigits(n);
        const padn = FormatDotNetStyle.formatCountPad(formatInteger, false);
        for (let i = s.length; i < padn; i++) s = '0' + s;

        let delimitGroups = false;
        for (let i = 1; i < formatInteger.length - 1; i++)
            if (
                formatInteger[i] == FormatDotNetStyle.groupCode &&
                (formatInteger[i - 1] == FormatDotNetStyle.zeroCode || formatInteger[i - 1] == FormatDotNetStyle.poundCode) &&
                (formatInteger[i + 1] == FormatDotNetStyle.zeroCode || formatInteger[i + 1] == FormatDotNetStyle.poundCode)
            ) {
                delimitGroups = true;
                break;
            }
        if (delimitGroups) {
            let cg = 0;
            let gsc = '';
            let gsr = '';
            for (let i = 0; i < this.localizedGroupSize.length - 1 && cg + this.localizedGroupSize[i] <= s.length; i++) {
                if (cg > 0) gsc += this.localizedGroupSymbol;
                gsc += s.substr(s.length - cg - this.localizedGroupSize[i], this.localizedGroupSize[i]);
                cg += this.localizedGroupSize[i];
            }
            const groupSize = this.localizedGroupSize[this.localizedGroupSize.length - 1];
            if (groupSize >= 0) {
                let groups = (s.length - cg) / groupSize;
                let lastGroupSize = (s.length - cg) % groupSize;
                if (lastGroupSize == 0 && groups > 0) {
                    groups--;
                    lastGroupSize = groupSize;
                }
                gsr = s.substr(0, lastGroupSize);
                for (let i = lastGroupSize; i < s.length - cg; i += groupSize) gsr = gsr + this.localizedGroupSymbol + s.substr(i, groupSize);
            } else {
                gsr = s.substr(0, s.length - cg);
            }
            s = gsr.length > 0 && gsc.length > 0 ? gsr + this.localizedGroupSymbol + gsc : gsr + gsc;
        }

        s = FormatDotNetStyle.formatInsertExtraSymbols(s, formatInteger, false);

        if (formatFloat.length > 0) {
            // && precision > 0
            if (formatE.length > 0) {
                const d = FormatDotNetStyle.formatCountNumeric(formatInteger);
                const numF = originalnum % Math.pow(10, d);
                a = Math.abs(a);
            }
            let m = 0;
            while (Math.floor(a) != a && m < precision) {
                a *= 10;
                n *= 10;
                m++;
            }
            const f = FormatDotNetStyle.formatRound(a - n, 0);
            let sf = '';
            if (f != 0) {
                sf = FormatDotNetStyle.toDigits(f);
                for (let i = sf.length; i < m; i++) sf = '0' + sf;
            }
            const padf = FormatDotNetStyle.formatCountPad(formatFloat, true);
            sf = FormatDotNetStyle.trimEnd0(sf);
            for (let i = sf.length; i < padf; i++) sf = sf + '0';

            if (sf.length > 0) sf = this.localizedDecimalSymbol + sf;
            sf = FormatDotNetStyle.formatInsertExtraSymbols(sf, formatFloat, true);
            if (sf.length > 0) s += sf;
        }

        let encoded = (num < 0 && !multipleFormats ? '-' : '') + s;
        if (formatE.length > 0) encoded = encoded.replace(FormatDotNetStyle.upperECode, formatE);
        return encoded;
    }
    private formatDotNetStyleCustom(format: string, num: number): string {
        let unescaped = '';
        let sq = false;
        let dq = false;
        for (let i = 0; i < format.length; i++) {
            if (sq) {
                if (format[i] == "'") sq = false;
                else unescaped += format[i];
            } else if (dq) {
                if (format[i] == '"') dq = false;
                else unescaped += format[i];
            } else {
                if (format[i] == "'") {
                    sq = true;
                } else if (format[i] == '"') {
                    dq = true;
                } else if (format[i] == '\\') {
                    if (i < format.length - 1) {
                        i++;
                        unescaped += format[i];
                    }
                } else {
                    if (format[i] == '.') unescaped += FormatDotNetStyle.decimalCode;
                    else if (format[i] == ',') unescaped += FormatDotNetStyle.groupCode;
                    else if (format[i] == '0') unescaped += FormatDotNetStyle.zeroCode;
                    else if (format[i] == '#') unescaped += FormatDotNetStyle.poundCode;
                    else if (format[i] == ';') unescaped += FormatDotNetStyle.sectionCode;
                    else if (format[i] == '%') unescaped += FormatDotNetStyle.percentCode;
                    //% & ‰ are used directly, that is how it is supposed to be, only these are considered format specifiers, not the localized versions
                    else if (format[i] == '‰') unescaped += FormatDotNetStyle.permilleCode;
                    else if (format[i] == 'e') unescaped += FormatDotNetStyle.lowerECode;
                    else if (format[i] == 'E') unescaped += FormatDotNetStyle.upperECode;
                    else unescaped += format[i];
                }
            }
        }

        const formats = unescaped.split(FormatDotNetStyle.sectionCode);
        const formatP = formats[0];
        const formatN = formats.length >= 2 && formats[1].length > 0 ? formats[1] : formatP;
        const formatZ = formats.length >= 3 && formats[2].length > 0 ? formats[2] : formatP;
        const f = num == 0 ? formatZ : num < 0 ? formatN : formatP;
        const multimpleFormats = (formats.length >= 3 && formats[2].length > 0) || (formats.length >= 2 && formats[1].length > 0);

        let encoded = this.formatEncoded(f, num, multimpleFormats);
        if (formats.length > 1 && num != 0 && encoded == '0') encoded = this.formatEncoded(formatZ, 0, multimpleFormats);

        let decoded = '';
        for (let i = 0; i < encoded.length; i++) {
            if (encoded[i] == FormatDotNetStyle.decimalCode) {
            } //decoded += this.this.localizedDecimalSymbol;
            else if (encoded[i] == FormatDotNetStyle.groupCode) {
            } //decoded += this.localizedGroupSymbol;
            else if (encoded[i] == FormatDotNetStyle.percentCode) decoded += this.localizedPercentSymbol;
            else if (encoded[i] == FormatDotNetStyle.permilleCode) decoded += this.localizedPermilleSymbol;
            //else if (encoded[i] == FormatNumberDotNetStyle.sectionCode) decoded += ';';
            else decoded += encoded[i];
        }
        return decoded;
    }
    public parseNumber(s: string): number {
        const divide =
            s.indexOf('%') >= 0 || s.indexOf(this.localizedPercentSymbol) >= 0
                ? 2
                : s.indexOf('‰') >= 0 || s.indexOf(this.localizedPermilleSymbol) >= 0
                ? 3
                : 0;
        const negative = s.indexOf('-') >= 0 || (s.indexOf('(') >= 0 && s.indexOf(')') >= 0) || s.indexOf(this.numberCulture.NegativeSign) >= 0;
        let dp = s.indexOf(this.localizedDecimalSymbol);
        const dp1 = s.indexOf('.');
        const dp2 = s.indexOf(',');
        if (dp1 >= 0 && dp1 > dp) dp = dp1;
        if (dp2 >= 0 && dp2 > dp) dp = dp2;
        if (dp >= 0) {
            const s1 = s.substr(0, dp);
            const s2 = s.substr(dp + 1);
            s = s1 + FormatDotNetStyle.decimalCode + s2;
        }
        let n = '';
        for (let i = 0; i < s.length; i++) {
            if (FormatDotNetStyle.isDigitAt(s, i)) n += s[i];
            else if (s[i] == FormatDotNetStyle.decimalCode) n += '.';
        }
        if (n.length == 0) return null;
        if (n[n.length - 1] == '.') n = n.substr(0, n.length - 1);
        if (negative) n = '-' + n;
        let r = parseFloat(n);
        if (isNaN(r) || !isFinite(r) || typeof r === undefined) r = null;
        if (r != null && divide > 0) r /= Math.pow(10, divide);
        return r;
    }
    public formatNumber(format: string, num: number): string {
        this.localizedGroupSize = this.numberCulture.NumberGroupSizes;
        this.localizedDecimalSymbol = this.numberCulture.NumberDecimalSeparator;
        this.localizedGroupSymbol = this.numberCulture.NumberGroupSeparator;
        this.localizedPercentSymbol = this.numberCulture.PercentSymbol;
        this.localizedPermilleSymbol = this.numberCulture.PerMilleSymbol;

        if (format.length == 0) format = 'G';

        const c0 = format[0];
        const c0u = c0.toUpperCase()[0];
        if ('CDEFXGNPR'.indexOf(c0u) < 0) return this.formatDotNetStyleCustom(format, num);
        let standard = true;
        for (let i = 1; i < format.length; i++)
            if (!FormatDotNetStyle.isDigitAt(format, i)) {
                standard = false;
                break;
            }
        if (!standard) return this.formatDotNetStyleCustom(format, num);

        let n = -1.0;
        if (format.length > 1) {
            n = parseInt(format.substr(1));
            if (n == Number.NaN) return this.formatDotNetStyleCustom(format, num);
        }
        if (n <= 0) n = -1;
        const n2 = FormatDotNetStyle.formatMulti('0', n != -1 ? n : 2);

        if (!isFinite(num)) return this.getNaNOrInfinitySymbol(num);

        if (c0u == 'N') {
            format = '#,0.' + FormatDotNetStyle.formatMulti('0', n != -1 ? n : this.numberCulture.NumberDecimalDigits);
            if (num >= 0) return this.formatDotNetStyleCustom(format, num);
            let format2 = '';
            if (this.numberCulture.NumberNegativePattern == 0) format2 = format + ';' + '(' + format + ')';
            if (this.numberCulture.NumberNegativePattern == 2) format2 = format + ';' + this.numberCulture.NegativeSign + ' ' + format;
            if (this.numberCulture.NumberNegativePattern == 3) format2 = format + ';' + format + this.numberCulture.NegativeSign;
            if (this.numberCulture.NumberNegativePattern == 4) format2 = format + ';' + format + ' ' + this.numberCulture.NegativeSign;
            format2 = format + ';' + this.numberCulture.NegativeSign + format; //1
            return this.formatDotNetStyleCustom(format2, num);
        }
        if (c0u == 'C') {
            this.localizedGroupSize = this.numberCulture.CurrencyGroupSizes;
            this.localizedDecimalSymbol = this.numberCulture.CurrencyDecimalSeparator;
            this.localizedGroupSymbol = this.numberCulture.CurrencyGroupSeparator;
            const currency = "'" + this.numberCulture.CurrencySymbol + "'";
            format = '#,0.' + FormatDotNetStyle.formatMulti('0', n != -1 ? n : this.numberCulture.CurrencyDecimalDigits);
            let format2 = '';
            if (this.numberCulture.CurrencyPositivePattern == 1) format2 = format + currency;
            else if (this.numberCulture.CurrencyPositivePattern == 2) format2 = currency + ' ' + format;
            else if (this.numberCulture.CurrencyPositivePattern == 3) format2 = format + ' ' + currency;
            else format2 = currency + format; //0
            if (num >= 0) return this.formatDotNetStyleCustom(format2, num);

            if (this.numberCulture.CurrencyNegativePattern == 1) format2 = format2 + ';' + this.numberCulture.NegativeSign + currency + format;
            else if (this.numberCulture.CurrencyNegativePattern == 2) format2 = format2 + ';' + currency + this.numberCulture.NegativeSign + format;
            else if (this.numberCulture.CurrencyNegativePattern == 3) format2 = format2 + ';' + currency + format + this.numberCulture.NegativeSign;
            else if (this.numberCulture.CurrencyNegativePattern == 4) format2 = format2 + ';' + '(' + format + currency + ')';
            else if (this.numberCulture.CurrencyNegativePattern == 5) format2 = format2 + ';' + this.numberCulture.NegativeSign + format + currency;
            else if (this.numberCulture.CurrencyNegativePattern == 6) format2 = format2 + ';' + format + this.numberCulture.NegativeSign + currency;
            else if (this.numberCulture.CurrencyNegativePattern == 7) format2 = format2 + ';' + format + currency + this.numberCulture.NegativeSign;
            else if (this.numberCulture.CurrencyNegativePattern == 8)
                format2 = format2 + ';' + this.numberCulture.NegativeSign + format + ' ' + currency;
            else if (this.numberCulture.CurrencyNegativePattern == 9)
                format2 = format2 + ';' + this.numberCulture.NegativeSign + currency + ' ' + format;
            else if (this.numberCulture.CurrencyNegativePattern == 10)
                format2 = format2 + ';' + format + ' ' + currency + this.numberCulture.NegativeSign;
            else if (this.numberCulture.CurrencyNegativePattern == 11)
                format2 = format2 + ';' + currency + ' ' + format + this.numberCulture.NegativeSign;
            else if (this.numberCulture.CurrencyNegativePattern == 12)
                format2 = format2 + ';' + currency + ' ' + this.numberCulture.NegativeSign + format;
            else if (this.numberCulture.CurrencyNegativePattern == 13)
                format2 = format2 + ';' + format + this.numberCulture.NegativeSign + ' ' + currency;
            else if (this.numberCulture.CurrencyNegativePattern == 14) format2 = format2 + ';' + '(' + currency + ' ' + format + ')';
            else if (this.numberCulture.CurrencyNegativePattern == 15) format2 = format2 + ';' + '(' + format + ' ' + currency + ')';
            else format2 = format2 + ';' + '(' + currency + format + ')'; //0
            return this.formatDotNetStyleCustom(format2, num);
        }
        if (c0u == 'P') {
            this.localizedGroupSize = this.numberCulture.PercentGroupSizes;
            this.localizedDecimalSymbol = this.numberCulture.PercentDecimalSeparator;
            this.localizedGroupSymbol = this.numberCulture.PercentGroupSeparator;
            format = '#,0.' + FormatDotNetStyle.formatMulti('0', n != -1 ? n : this.numberCulture.PercentDecimalDigits);
            let format2 = '';
            if (this.numberCulture.PercentPositivePattern == 0) format2 = format + ' ' + this.numberCulture.PercentSymbol;
            else if (this.numberCulture.PercentPositivePattern == 2) format2 = this.numberCulture.PercentSymbol + format;
            else if (this.numberCulture.PercentPositivePattern == 3) format2 = this.numberCulture.PercentSymbol + ' ' + format;
            else format2 = format + this.numberCulture.PercentSymbol; //1
            if (num >= 0) return this.formatDotNetStyleCustom(format2, num);

            if (this.numberCulture.PercentNegativePattern == 0) format2 = format2 + ';' + this.numberCulture.NegativeSign + format + ' %';
            else if (this.numberCulture.PercentNegativePattern == 2) format2 = format2 + ';' + this.numberCulture.NegativeSign + '%' + format;
            else if (this.numberCulture.PercentNegativePattern == 3) format2 = format2 + ';' + '%' + this.numberCulture.NegativeSign + format;
            else if (this.numberCulture.PercentNegativePattern == 4) format2 = format2 + ';' + '%' + format + this.numberCulture.NegativeSign;
            else if (this.numberCulture.PercentNegativePattern == 5) format2 = format2 + ';' + format + this.numberCulture.NegativeSign + '%';
            else if (this.numberCulture.PercentNegativePattern == 6) format2 = format2 + ';' + format + '%' + this.numberCulture.NegativeSign;
            else if (this.numberCulture.PercentNegativePattern == 7) format2 = format2 + ';' + this.numberCulture.NegativeSign + '% ' + format;
            else if (this.numberCulture.PercentNegativePattern == 8) format2 = format2 + ';' + format + ' %' + this.numberCulture.NegativeSign;
            else if (this.numberCulture.PercentNegativePattern == 9) format2 = format2 + ';' + '% ' + format + this.numberCulture.NegativeSign;
            else if (this.numberCulture.PercentNegativePattern == 10) format2 = format2 + ';' + '% ' + this.numberCulture.NegativeSign + format;
            else if (this.numberCulture.PercentNegativePattern == 11) format2 = format2 + ';' + format + this.numberCulture.NegativeSign + ' %';
            else format2 = format2 + ';' + this.numberCulture.NegativeSign + format + '%'; //1
            return this.formatDotNetStyleCustom(format2, num);
        }
        if (c0u == 'G' || c0u == 'R') {
            if (c0u == 'R' || n == -1 || n > 15) n = 15;
            const e = FormatDotNetStyle.getActualExponent(num);
            if (e >= n || e <= -5) {
                return this.formatDotNetStyleCustom('0.' + FormatDotNetStyle.formatMulti('#', n - 1) + 'E+00', num);
            } else {
                return this.formatDotNetStyleCustom('0.' + FormatDotNetStyle.formatMulti('#', n - e - 1), num);
            }
        }

        if (c0u == 'D') format = FormatDotNetStyle.formatMulti('0', n != -1 ? n : 1);
        else if (c0u == 'E') format = '0.' + FormatDotNetStyle.formatMulti('0', n != -1 ? n : 6) + FormatDotNetStyle.upperECode + '+000';
        else if (c0u == 'F') format = '0.' + n2;

        return this.formatDotNetStyleCustom(format, num);
    }

    public formatTimeSpan(format: string, ticks: number): string {
        if (format.length > 1 || 'cgG'.indexOf(format) < 0) return this.formatTimeSpanCustom(format, ticks);
        if (format.length == 0) format = 'c';
        if (format == 'g')
            return this.formatTimeSpanCustom('-H\\:h\\:mm\\:ss.MMMMMMM'.replace('.', this.numberCulture.NumberDecimalSeparator), ticks);
        if (format == 'G')
            return this.formatTimeSpanCustom('-d\\:hh\\:mm\\:ss.fffffff'.replace('.', this.numberCulture.NumberDecimalSeparator), ticks);
        /*if (format == "G")*/
        return this.formatTimeSpanCustom('-H.hh\\:mm\\:ss\\.ggggggg', ticks);
    }
    public parseTimeSpan(timespan: string): number {
        this.position = 0;
        this.actualPosition = 0;
        this.source = timespan.toUpperCase();
        let negative = false;
        let days = -1;
        let hours = -1;
        let minutes = -1;
        let seconds = -1;
        let milliseconds = -1;
        if (this.source.length == 0) return 0;
        if (this.source.length > 0 && this.source[0] == '-') negative = true;
        let v = this.scanNumber();
        if (v < 0) return 0;

        if (this.actualPosition < this.source.length && this.source.substr(this.actualPosition, 1) == ':' && this.source.split(':').length < 4) {
            hours = v;
        } else if (this.actualPosition < this.source.length && this.source.substr(this.actualPosition, 1) == 'H') {
            hours = v;
        } else if (this.actualPosition < this.source.length - 1 && this.source.substr(this.actualPosition, 2) == 'MS') {
            milliseconds = v;
        } else if (this.actualPosition < this.source.length && this.source.substr(this.actualPosition, 1) == 'M') {
            minutes = v;
        } else if (this.actualPosition < this.source.length && this.source.substr(this.actualPosition, 1) == 'S') {
            seconds = v;
        } else {
            days = v;
        }

        if (days >= 0) {
            v = this.scanNumber();
            if (v >= 0) hours = v;
        } else days = 0;
        if (hours >= 0) {
            v = this.scanNumber();
            if (v >= 0) minutes = v;
        } else hours = 0;
        if (minutes >= 0) {
            v = this.scanNumber();
            if (v >= 0) seconds = v;
        } else minutes = 0;
        if (seconds >= 0) {
            v = this.scanNumberLimited(3);
            if (v >= 0) milliseconds = v;
        } else seconds = 0;
        if (milliseconds >= 0) {
        } else milliseconds = 0;

        let ticks =
            days * 10000000.0 * 60 * 60 * 24 + hours * 10000000.0 * 60 * 60 + minutes * 10000000.0 * 60 + seconds * 10000000.0 + milliseconds * 10000;
        if (negative) ticks = -1 * ticks;
        return ticks;
    }
    private formatTimeSpanCustom(format: string, ticks: number): string {
        const negative = ticks < 0;
        ticks = Math.abs(ticks);
        const days = Math.floor(ticks / (10000000.0 * 60 * 60 * 24));
        const hours = Math.floor((ticks - days * 10000000.0 * 60 * 60 * 24) / (10000000.0 * 60 * 60));
        const minutes = Math.floor((ticks - days * 10000000.0 * 60 * 60 * 24 - hours * 10000000.0 * 60 * 60) / (10000000.0 * 60));
        const seconds = Math.floor(
            (ticks - days * 10000000.0 * 60 * 60 * 24 - hours * 10000000.0 * 60 * 60 - minutes * 10000000.0 * 60) / 10000000.0
        );
        const tt = Math.floor(
            ticks - days * 10000000.0 * 60 * 60 * 24 - hours * 10000000.0 * 60 * 60 - minutes * 10000000.0 * 60 - seconds * 10000000.0
        );

        let v = false;
        let minus = false;
        const unescaped = this.unescapeTimeSpan(format);
        let s = '';
        this.position = 0;
        while (this.position < unescaped.length) {
            if (this.match(unescaped, FormatDotNetStyle.minusCode)) {
                minus = true;
            } else if (this.match(unescaped, FormatDotNetStyle.dCode)) {
                let n = 1;
                while (this.match(unescaped, FormatDotNetStyle.dCode)) n++;
                s += FormatDotNetStyle.pad0(days, n);
                v = v || days > 0;
            } else if (this.match(unescaped, FormatDotNetStyle.HCode)) {
                let n = 1;
                while (this.match(unescaped, FormatDotNetStyle.HCode)) n++;
                if (days != 0) {
                    s += FormatDotNetStyle.pad0(days, n);
                    v = true;
                } else {
                    if (this.match(unescaped, '.')) {
                    } else if (this.match(unescaped, ':')) {
                    } else if (this.match(unescaped, this.numberCulture.NumberDecimalSeparator)) {
                    }
                }
            } else if (this.match(unescaped, FormatDotNetStyle.hhCode)) {
                s += FormatDotNetStyle.pad0(hours, 2);
                while (this.match(unescaped, FormatDotNetStyle.hCode));
                v = v || hours > 0;
            } else if (this.match(unescaped, FormatDotNetStyle.hCode)) {
                s += hours;
                v = v || hours > 0;
            } else if (this.match(unescaped, FormatDotNetStyle.mmCode)) {
                s += FormatDotNetStyle.pad0(minutes, 2);
                while (this.match(unescaped, FormatDotNetStyle.mCode));
                v = v || minutes > 0;
            } else if (this.match(unescaped, FormatDotNetStyle.mCode)) {
                s += minutes;
                v = v || minutes > 0;
            } else if (this.match(unescaped, FormatDotNetStyle.ssCode)) {
                s += FormatDotNetStyle.pad0(seconds, 2);
                while (this.match(unescaped, FormatDotNetStyle.sCode));
            } else if (this.match(unescaped, FormatDotNetStyle.sCode)) {
                s += seconds;
                v = v || seconds > 0;
            } else if (this.match(unescaped, FormatDotNetStyle.fCode)) {
                let n = 1;
                while (this.match(unescaped, FormatDotNetStyle.fCode)) n++;
                const t = Math.floor(tt / (1000000 / Math.pow(10, n - 1)));
                v = v || t > 0;
                s += FormatDotNetStyle.pad0(t, n);
            } else if (this.match(unescaped, FormatDotNetStyle.FCode)) {
                let n = 1;
                while (this.match(unescaped, FormatDotNetStyle.FCode)) n++;
                const t = Math.floor(tt / (1000000 / Math.pow(10, n - 1)));
                v = v || t > 0;
                s += FormatDotNetStyle.pad0t(t, n);
            } else if (this.match(unescaped, FormatDotNetStyle.gCode)) {
                let n = 1;
                while (this.match(unescaped, FormatDotNetStyle.gCode)) n++;
                const t = Math.floor(tt / (1000000 / Math.pow(10, n - 1)));
                v = v || t > 0;
                const f = FormatDotNetStyle.pad0(t, n);
                if (FormatDotNetStyle.trimEnd0(f) == '') s = this.trimF(s);
                else s += f;
            } else if (this.match(unescaped, FormatDotNetStyle.MCode)) {
                let n = 1;
                while (this.match(unescaped, FormatDotNetStyle.MCode)) n++;
                const t = Math.floor(tt / (1000000 / Math.pow(10, n - 1)));
                v = v || t > 0;
                const f = FormatDotNetStyle.pad0t(t, n);
                if (FormatDotNetStyle.trimEnd0(f) == '') s = this.trimF(s);
                else s += f;
            } else {
                s += unescaped[this.position];
                this.position++;
            }
        }
        return (negative && minus && v ? '-' : '') + s;
    }
    private scanWord(): string {
        let w = '';
        while (this.actualPosition < this.source.length) {
            const l = FormatDotNetStyle.isLetterAt(this.source, this.actualPosition);
            if (l) {
                w += this.source[this.actualPosition];
            } else {
                if (w.length > 0) {
                    return w;
                } else {
                }
            }
            this.actualPosition++;
        }
        return w;
    }
    private scanNumber(): number {
        return this.scanNumberLimited(1000000);
    }
    private scanNumberLimited(digitLimit: number): number {
        let n = -1;
        this.digitCount = 0;
        while (this.actualPosition < this.source.length) {
            const d = FormatDotNetStyle.getDigitAt(this.source, this.actualPosition);
            if (d >= 0) {
                this.digitCount++;
                if (this.digitCount > digitLimit) break;
            }
            if (n < 0) {
                if (d < 0) {
                } else {
                    n = d;
                }
            } else {
                if (d < 0) {
                    break;
                } else {
                    n = n * 10 + d;
                }
            }
            this.actualPosition++;
        }
        return n;
    }
    private unescapeTimeSpan(format: string): string {
        let unescaped = '';
        let sq = false;
        let dq = false;
        for (let i = 0; i < format.length; i++) {
            if (sq) {
                if (format[i] == "'") sq = false;
                else unescaped += format[i];
            } else if (dq) {
                if (format[i] == '"') dq = false;
                else unescaped += format[i];
            } else {
                if (format[i] == "'") {
                    sq = true;
                } else if (format[i] == '"') {
                    dq = true;
                } else if (format[i] == '\\') {
                    if (i < format.length - 1) {
                        i++;
                        unescaped += format[i];
                    }
                } else {
                    if (format[i] == '%') {
                    } else if (format[i] == '-') unescaped += FormatDotNetStyle.minusCode;
                    else if (format[i] == 'd') unescaped += FormatDotNetStyle.dCode;
                    else if (format[i] == 'H') unescaped += FormatDotNetStyle.HCode;
                    //this hack is used to introduce optional days
                    else if (format[i] == 'h') unescaped += FormatDotNetStyle.hCode;
                    else if (format[i] == 'm') unescaped += FormatDotNetStyle.mCode;
                    else if (format[i] == 's') unescaped += FormatDotNetStyle.sCode;
                    else if (format[i] == 'f') unescaped += FormatDotNetStyle.fCode;
                    else if (format[i] == 'g') unescaped += FormatDotNetStyle.gCode;
                    else if (format[i] == 'F') unescaped += FormatDotNetStyle.FCode;
                    else if (format[i] == 'M') unescaped += FormatDotNetStyle.MCode;
                    else unescaped += format[i];
                }
            }
        }
        return unescaped;
    }
}

FormatDotNetStyle.numberCultures['ru'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-бесконечность',
    PositiveInfinitySymbol: 'бесконечность',
    NaNSymbol: 'NaN',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 5,
    CurrencyPositivePattern: 1,
    CurrencyDecimalSeparator: ',',
    CurrencyGroupSeparator: ' ',
    CurrencySymbol: 'р.',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: ',',
    NumberGroupSeparator: ' ',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 1,
    PercentPositivePattern: 1,
    PercentDecimalSeparator: ',',
    PercentGroupSeparator: ' ',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['en-us'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-Infinity',
    PositiveInfinitySymbol: 'Infinity',
    NaNSymbol: 'NaN',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 0,
    CurrencyPositivePattern: 0,
    CurrencyDecimalSeparator: '.',
    CurrencyGroupSeparator: ',',
    CurrencySymbol: '$',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: '.',
    NumberGroupSeparator: ',',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 0,
    PercentPositivePattern: 0,
    PercentDecimalSeparator: '.',
    PercentGroupSeparator: ',',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['en-gb'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-Infinity',
    PositiveInfinitySymbol: 'Infinity',
    NaNSymbol: 'NaN',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 1,
    CurrencyPositivePattern: 0,
    CurrencyDecimalSeparator: '.',
    CurrencyGroupSeparator: ',',
    CurrencySymbol: '£',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: '.',
    NumberGroupSeparator: ',',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 0,
    PercentPositivePattern: 0,
    PercentDecimalSeparator: '.',
    PercentGroupSeparator: ',',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['cs'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-nekonečno',
    PositiveInfinitySymbol: '+nekonečno',
    NaNSymbol: 'Není číslo',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 8,
    CurrencyPositivePattern: 3,
    CurrencyDecimalSeparator: ',',
    CurrencyGroupSeparator: ' ',
    CurrencySymbol: 'Kč',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: ',',
    NumberGroupSeparator: ' ',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 1,
    PercentPositivePattern: 1,
    PercentDecimalSeparator: ',',
    PercentGroupSeparator: ' ',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['pt'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-Infinito',
    PositiveInfinitySymbol: '+Infinito',
    NaNSymbol: 'NaN (Não é um número)',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 9,
    CurrencyPositivePattern: 2,
    CurrencyDecimalSeparator: ',',
    CurrencyGroupSeparator: '.',
    CurrencySymbol: 'R$',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: ',',
    NumberGroupSeparator: '.',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 1,
    PercentPositivePattern: 1,
    PercentDecimalSeparator: ',',
    PercentGroupSeparator: '.',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['de'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-unendlich',
    PositiveInfinitySymbol: '+unendlich',
    NaNSymbol: 'n. def.',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 8,
    CurrencyPositivePattern: 3,
    CurrencyDecimalSeparator: ',',
    CurrencyGroupSeparator: '.',
    CurrencySymbol: '€',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: ',',
    NumberGroupSeparator: '.',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 1,
    PercentPositivePattern: 1,
    PercentDecimalSeparator: ',',
    PercentGroupSeparator: '.',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['es'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-Infinito',
    PositiveInfinitySymbol: 'Infinito',
    NaNSymbol: 'NeuN',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 8,
    CurrencyPositivePattern: 3,
    CurrencyDecimalSeparator: ',',
    CurrencyGroupSeparator: '.',
    CurrencySymbol: '€',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: ',',
    NumberGroupSeparator: '.',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 0,
    PercentPositivePattern: 0,
    PercentDecimalSeparator: ',',
    PercentGroupSeparator: '.',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['fr'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-Infini',
    PositiveInfinitySymbol: '+Infini',
    NaNSymbol: 'Non Numérique',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 8,
    CurrencyPositivePattern: 3,
    CurrencyDecimalSeparator: ',',
    CurrencyGroupSeparator: ' ',
    CurrencySymbol: '€',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: ',',
    NumberGroupSeparator: ' ',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 0,
    PercentPositivePattern: 0,
    PercentDecimalSeparator: ',',
    PercentGroupSeparator: ' ',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['it'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-Infinito',
    PositiveInfinitySymbol: '+Infinito',
    NaNSymbol: 'Non un numero reale',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 9,
    CurrencyPositivePattern: 2,
    CurrencyDecimalSeparator: ',',
    CurrencyGroupSeparator: '.',
    CurrencySymbol: '€',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: ',',
    NumberGroupSeparator: '.',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 1,
    PercentPositivePattern: 1,
    PercentDecimalSeparator: ',',
    PercentGroupSeparator: '.',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['ja'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-∞',
    PositiveInfinitySymbol: '+∞',
    NaNSymbol: 'NaN (非数値)',
    CurrencyDecimalDigits: 0,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 1,
    CurrencyPositivePattern: 0,
    CurrencyDecimalSeparator: '.',
    CurrencyGroupSeparator: ',',
    CurrencySymbol: '¥',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: '.',
    NumberGroupSeparator: ',',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 1,
    PercentPositivePattern: 1,
    PercentDecimalSeparator: '.',
    PercentGroupSeparator: ',',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['sv'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-INF',
    PositiveInfinitySymbol: 'INF',
    NaNSymbol: 'NaN',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 8,
    CurrencyPositivePattern: 3,
    CurrencyDecimalSeparator: ',',
    CurrencyGroupSeparator: '.',
    CurrencySymbol: 'kr',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: ',',
    NumberGroupSeparator: ' ',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 0,
    PercentPositivePattern: 0,
    PercentDecimalSeparator: ',',
    PercentGroupSeparator: ' ',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['pl'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '-nieskończoność',
    PositiveInfinitySymbol: '+nieskończoność',
    NaNSymbol: 'nie jest liczbą',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 8,
    CurrencyPositivePattern: 3,
    CurrencyDecimalSeparator: ',',
    CurrencyGroupSeparator: ' ',
    CurrencySymbol: 'zł',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: ',',
    NumberGroupSeparator: ' ',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 1,
    PercentPositivePattern: 1,
    PercentDecimalSeparator: ',',
    PercentGroupSeparator: ' ',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.numberCultures['zh'] = {
    NegativeSign: '-',
    PositiveSign: '+',
    NegativeInfinitySymbol: '负无穷大',
    PositiveInfinitySymbol: '正无穷大',
    NaNSymbol: '非数字',
    CurrencyDecimalDigits: 2,
    CurrencyGroupSizes: [3],
    CurrencyNegativePattern: 2,
    CurrencyPositivePattern: 0,
    CurrencyDecimalSeparator: '.',
    CurrencyGroupSeparator: ',',
    CurrencySymbol: '¥',
    NumberDecimalDigits: 2,
    NumberGroupSizes: [3],
    NumberNegativePattern: 1,
    NumberDecimalSeparator: '.',
    NumberGroupSeparator: ',',
    PercentDecimalDigits: 2,
    PercentGroupSizes: [3],
    PercentNegativePattern: 1,
    PercentPositivePattern: 1,
    PercentDecimalSeparator: '.',
    PercentGroupSeparator: ',',
    PercentSymbol: '%',
    PerMilleSymbol: '‰'
};
FormatDotNetStyle.dateTimeCultures['pt'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['d.C.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'Calendário gregoriano',
    AbbreviatedDayNames: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'],
    DayNames: ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'],
    ShortestDayNames: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
    MonthNames: ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro', ''],
    MonthGenitiveNames: [
        'janeiro',
        'fevereiro',
        'março',
        'abril',
        'maio',
        'junho',
        'julho',
        'agosto',
        'setembro',
        'outubro',
        'novembro',
        'dezembro',
        ''
    ],
    AbbreviatedMonthNames: ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez', ''],
    AbbreviatedMonthGenitiveNames: ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez', ''],
    AMDesignator: '',
    PMDesignator: '',
    DateSeparator: '/',
    TimeSeparator: ':',
    CalendarWeekRule: 0,
    FirstDayOfWeek: 0,
    FullDateTimePattern: "dddd, d' de 'MMMM' de 'yyyy HH:mm:ss",
    LongDatePattern: "dddd, d' de 'MMMM' de 'yyyy",
    LongTimePattern: 'HH:mm:ss',
    MonthDayPattern: "dd' de 'MMMM",
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'dd/MM/yyyy',
    ShortTimePattern: 'HH:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: "MMMM' de 'yyyy",
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'Q',
    LongQuarter: 'Trimestre',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['ru'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['A.D.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'григорианский календарь',
    AbbreviatedDayNames: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
    DayNames: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'],
    ShortestDayNames: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
    MonthNames: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь', ''],
    MonthGenitiveNames: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря', ''],
    AbbreviatedMonthNames: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек', ''],
    AbbreviatedMonthGenitiveNames: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек', ''],
    AMDesignator: '',
    PMDesignator: '',
    DateSeparator: '.',
    TimeSeparator: ':',
    CalendarWeekRule: 0,
    FirstDayOfWeek: 1,
    FullDateTimePattern: "d MMMM yyyy 'г.' H:mm:ss",
    LongDatePattern: "d MMMM yyyy 'г.'",
    LongTimePattern: 'H:mm:ss',
    MonthDayPattern: 'MMMM dd',
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'dd.MM.yyyy',
    ShortTimePattern: 'H:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: 'MMMM yyyy',
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'К',
    LongQuarter: 'Квартал',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['es'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['d.C.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'calendario gregoriano',
    AbbreviatedDayNames: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    DayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    ShortestDayNames: ['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sá'],
    MonthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre', ''],
    MonthGenitiveNames: [
        'enero',
        'febrero',
        'marzo',
        'abril',
        'mayo',
        'junio',
        'julio',
        'agosto',
        'septiembre',
        'octubre',
        'noviembre',
        'diciembre',
        ''
    ],
    AbbreviatedMonthNames: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic', ''],
    AbbreviatedMonthGenitiveNames: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic', ''],
    AMDesignator: '',
    PMDesignator: '',
    DateSeparator: '/',
    TimeSeparator: ':',
    CalendarWeekRule: 0,
    FirstDayOfWeek: 1,
    FullDateTimePattern: "dddd, dd' de 'MMMM' de 'yyyy H:mm:ss",
    LongDatePattern: "dddd, dd' de 'MMMM' de 'yyyy",
    LongTimePattern: 'H:mm:ss',
    MonthDayPattern: 'dd MMMM',
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'dd/MM/yyyy',
    ShortTimePattern: 'H:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: "MMMM' de 'yyyy",
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'Q',
    LongQuarter: 'Trimestre',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['en-us'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['A.D.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'Gregorian Calendar',
    AbbreviatedDayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    DayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    ShortestDayNames: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    MonthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', ''],
    MonthGenitiveNames: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
        ''
    ],
    AbbreviatedMonthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', ''],
    AbbreviatedMonthGenitiveNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', ''],
    AMDesignator: 'AM',
    PMDesignator: 'PM',
    DateSeparator: '/',
    TimeSeparator: ':',
    CalendarWeekRule: 0,
    FirstDayOfWeek: 0,
    FullDateTimePattern: 'dddd, MMMM dd, yyyy h:mm:ss tt',
    LongDatePattern: 'dddd, MMMM dd, yyyy',
    LongTimePattern: 'h:mm:ss tt',
    MonthDayPattern: 'MMMM dd',
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'M/d/yyyy',
    ShortTimePattern: 'h:mm tt',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: 'MMMM, yyyy',
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'Q',
    LongQuarter: 'Quarter',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['cs'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['n. l.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'gregoriánský kalendář',
    AbbreviatedDayNames: ['ne', 'po', 'út', 'st', 'čt', 'pá', 'so'],
    DayNames: ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'],
    ShortestDayNames: ['ne', 'po', 'út', 'st', 'čt', 'pá', 'so'],
    MonthNames: ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec', ''],
    MonthGenitiveNames: ['ledna', 'února', 'března', 'dubna', 'května', 'června', 'července', 'srpna', 'září', 'října', 'listopadu', 'prosince', ''],
    AbbreviatedMonthNames: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', ''],
    AbbreviatedMonthGenitiveNames: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', ''],
    AMDesignator: 'dop.',
    PMDesignator: 'odp.',
    DateSeparator: '.',
    TimeSeparator: ':',
    CalendarWeekRule: 0,
    FirstDayOfWeek: 1,
    FullDateTimePattern: 'd. MMMM yyyy H:mm:ss',
    LongDatePattern: 'd. MMMM yyyy',
    LongTimePattern: 'H:mm:ss',
    MonthDayPattern: 'dd MMMM',
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'd.M.yyyy',
    ShortTimePattern: 'H:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: 'MMMM yyyy',
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'Q',
    LongQuarter: 'Čtvrtletí',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['en-gb'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['A.D.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'Gregorian Calendar',
    AbbreviatedDayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    DayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    ShortestDayNames: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    MonthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', ''],
    MonthGenitiveNames: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
        ''
    ],
    AbbreviatedMonthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', ''],
    AbbreviatedMonthGenitiveNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', ''],
    AMDesignator: 'AM',
    PMDesignator: 'PM',
    DateSeparator: '/',
    TimeSeparator: ':',
    CalendarWeekRule: 0,
    FirstDayOfWeek: 1,
    FullDateTimePattern: 'dd MMMM yyyy HH:mm:ss',
    LongDatePattern: 'dd MMMM yyyy',
    LongTimePattern: 'HH:mm:ss',
    MonthDayPattern: 'dd MMMM',
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'dd/MM/yyyy',
    ShortTimePattern: 'HH:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: 'MMMM yyyy',
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'Q',
    LongQuarter: 'Quarter',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['de'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['n. Chr.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'Gregorianischer Kalender',
    AbbreviatedDayNames: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    DayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
    ShortestDayNames: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    MonthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember', ''],
    MonthGenitiveNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember', ''],
    AbbreviatedMonthNames: ['Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez', ''],
    AbbreviatedMonthGenitiveNames: ['Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez', ''],
    AMDesignator: '',
    PMDesignator: '',
    DateSeparator: '.',
    TimeSeparator: ':',
    CalendarWeekRule: 2,
    FirstDayOfWeek: 1,
    FullDateTimePattern: 'dddd, d. MMMM yyyy HH:mm:ss',
    LongDatePattern: 'dddd, d. MMMM yyyy',
    LongTimePattern: 'HH:mm:ss',
    MonthDayPattern: 'dd MMMM',
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'dd.MM.yyyy',
    ShortTimePattern: 'HH:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: 'MMMM yyyy',
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'Q',
    LongQuarter: 'Quartal',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['fr'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['ap. J.-C.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'calendrier grégorien',
    AbbreviatedDayNames: ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'],
    DayNames: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
    ShortestDayNames: ['di', 'lu', 'ma', 'me', 'je', 've', 'sa'],
    MonthNames: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre', ''],
    MonthGenitiveNames: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre', ''],
    AbbreviatedMonthNames: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.', ''],
    AbbreviatedMonthGenitiveNames: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.', ''],
    AMDesignator: '',
    PMDesignator: '',
    DateSeparator: '/',
    TimeSeparator: ':',
    CalendarWeekRule: 0,
    FirstDayOfWeek: 1,
    FullDateTimePattern: 'dddd d MMMM yyyy HH:mm:ss',
    LongDatePattern: 'dddd d MMMM yyyy',
    LongTimePattern: 'HH:mm:ss',
    MonthDayPattern: 'd MMMM',
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'dd/MM/yyyy',
    ShortTimePattern: 'HH:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: 'MMMM yyyy',
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'Q',
    LongQuarter: 'Trimestre',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['it'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['d.C.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'calendario gregoriano',
    AbbreviatedDayNames: ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'],
    DayNames: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
    ShortestDayNames: ['do', 'lu', 'ma', 'me', 'gi', 've', 'sa'],
    MonthNames: [
        'gennaio',
        'febbraio',
        'marzo',
        'aprile',
        'maggio',
        'giugno',
        'luglio',
        'agosto',
        'settembre',
        'ottobre',
        'novembre',
        'dicembre',
        ''
    ],
    MonthGenitiveNames: [
        'gennaio',
        'febbraio',
        'marzo',
        'aprile',
        'maggio',
        'giugno',
        'luglio',
        'agosto',
        'settembre',
        'ottobre',
        'novembre',
        'dicembre',
        ''
    ],
    AbbreviatedMonthNames: ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic', ''],
    AbbreviatedMonthGenitiveNames: ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic', ''],
    AMDesignator: '',
    PMDesignator: '',
    DateSeparator: '/',
    TimeSeparator: ':',
    CalendarWeekRule: 2,
    FirstDayOfWeek: 1,
    FullDateTimePattern: 'dddd d MMMM yyyy HH:mm:ss',
    LongDatePattern: 'dddd d MMMM yyyy',
    LongTimePattern: 'HH:mm:ss',
    MonthDayPattern: 'dd MMMM',
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'dd/MM/yyyy',
    ShortTimePattern: 'HH:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: 'MMMM yyyy',
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'Q',
    LongQuarter: 'Trimestre',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['ja'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['西暦'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: '西暦 (日本語)',
    AbbreviatedDayNames: ['日', '月', '火', '水', '木', '金', '土'],
    DayNames: ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'],
    ShortestDayNames: ['日', '月', '火', '水', '木', '金', '土'],
    MonthNames: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月', ''],
    MonthGenitiveNames: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月', ''],
    AbbreviatedMonthNames: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', ''],
    AbbreviatedMonthGenitiveNames: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', ''],
    AMDesignator: '午前',
    PMDesignator: '午後',
    DateSeparator: '/',
    TimeSeparator: ':',
    CalendarWeekRule: 0,
    FirstDayOfWeek: 0,
    FullDateTimePattern: "yyyy'年'M'月'd'日' H:mm:ss",
    LongDatePattern: "yyyy'年'M'月'd'日'",
    LongTimePattern: 'H:mm:ss',
    MonthDayPattern: "M'月'd'日'",
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'yyyy/MM/dd',
    ShortTimePattern: 'H:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: "yyyy'年'M'月'",
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: '四半期',
    LongQuarter: '四半期',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['sv'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['A.D.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'gregoriansk kalender',
    AbbreviatedDayNames: ['sö', 'må', 'ti', 'on', 'to', 'fr', 'lö'],
    DayNames: ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'],
    ShortestDayNames: ['sö', 'må', 'ti', 'on', 'to', 'fr', 'lö'],
    MonthNames: ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december', ''],
    MonthGenitiveNames: [
        'januari',
        'februari',
        'mars',
        'april',
        'maj',
        'juni',
        'juli',
        'augusti',
        'september',
        'oktober',
        'november',
        'december',
        ''
    ],
    AbbreviatedMonthNames: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec', ''],
    AbbreviatedMonthGenitiveNames: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec', ''],
    AMDesignator: '',
    PMDesignator: '',
    DateSeparator: '-',
    TimeSeparator: ':',
    CalendarWeekRule: 2,
    FirstDayOfWeek: 1,
    FullDateTimePattern: "'den 'd MMMM yyyy HH:mm:ss",
    LongDatePattern: "'den 'd MMMM yyyy",
    LongTimePattern: 'HH:mm:ss',
    MonthDayPattern: "'den 'd MMMM",
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'yyyy-MM-dd',
    ShortTimePattern: 'HH:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: 'MMMM yyyy',
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'Kv',
    LongQuarter: 'Kvartal',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['pl'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['A.D.'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: 'Kalendarz gregoriański',
    AbbreviatedDayNames: ['N', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'],
    DayNames: ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'],
    ShortestDayNames: ['N', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'],
    MonthNames: [
        'styczeń',
        'luty',
        'marzec',
        'kwiecień',
        'maj',
        'czerwiec',
        'lipiec',
        'sierpień',
        'wrzesień',
        'październik',
        'listopad',
        'grudzień',
        ''
    ],
    MonthGenitiveNames: [
        'stycznia',
        'lutego',
        'marca',
        'kwietnia',
        'maja',
        'czerwca',
        'lipca',
        'sierpnia',
        'września',
        'października',
        'listopada',
        'grudnia',
        ''
    ],
    AbbreviatedMonthNames: ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru', ''],
    AbbreviatedMonthGenitiveNames: ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru', ''],
    AMDesignator: '',
    PMDesignator: '',
    DateSeparator: '-',
    TimeSeparator: ':',
    CalendarWeekRule: 2,
    FirstDayOfWeek: 1,
    FullDateTimePattern: 'd MMMM yyyy HH:mm:ss',
    LongDatePattern: 'd MMMM yyyy',
    LongTimePattern: 'HH:mm:ss',
    MonthDayPattern: 'd MMMM',
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'yyyy-MM-dd',
    ShortTimePattern: 'HH:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: 'MMMM yyyy',
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: 'Kw',
    LongQuarter: 'Kwartał',
    Quarters: ['I', 'II', 'III', 'IV']
};
FormatDotNetStyle.dateTimeCultures['zh'] = {
    Calendar: {
        Eras: [1],
        EraNames: ['公元'],
        GetEra: function (x) {
            return 0;
        }
    },
    GetEraName: function (x) {
        return this.Calendar.EraNames[x];
    },
    NativeCalendarName: '公历',
    AbbreviatedDayNames: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
    DayNames: ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'],
    ShortestDayNames: ['日', '一', '二', '三', '四', '五', '六'],
    MonthNames: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月', ''],
    MonthGenitiveNames: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月', ''],
    AbbreviatedMonthNames: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月', ''],
    AbbreviatedMonthGenitiveNames: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月', ''],
    AMDesignator: '上午',
    PMDesignator: '下午',
    DateSeparator: '/',
    TimeSeparator: ':',
    CalendarWeekRule: 0,
    FirstDayOfWeek: 0,
    FullDateTimePattern: "yyyy'年'M'月'd'日' H:mm:ss",
    LongDatePattern: "yyyy'年'M'月'd'日'",
    LongTimePattern: 'H:mm:ss',
    MonthDayPattern: "M'月'd'日'",
    RFC1123Pattern: "ddd, dd MMM yyyy HH':'mm':'ss 'GMT'",
    ShortDatePattern: 'yyyy/M/d',
    ShortTimePattern: 'H:mm',
    SortableDateTimePattern: "yyyy'-'MM'-'dd'T'HH':'mm':'ss",
    YearMonthPattern: "yyyy'年'M'月'",
    UniversalSortableDateTimePattern: "yyyy'-'MM'-'dd HH':'mm':'ss'Z'",
    ShortQuarter: '季度',
    LongQuarter: '季度',
    Quarters: ['I', 'II', 'III', 'IV']
};
